import { useAppSelector } from '@app/hooks';
import { RootState } from '@app/store';
import { formatValue } from '@features/home-utility-monitoring/utils';
import { ManifestDevice, Sensor } from '@features/plan-manifest/types';
import { getUnitsByCategoryMeasure } from '@features/unit-settings/utils';
import {
  Category,
  Measure,
  Unit,
  convertValue,
  findMeasureLabel,
  findUnitsLabel,
  formatCategoryMeasurePair,
} from '@lib/labels';
import { Card, CardActionArea, CardContent, Chip, CircularProgress, Stack, Typography } from '@mui/material';
import { ReactNode, useCallback, useMemo } from 'react';
import Chart from 'react-apexcharts';
import { useIntervalReadingsQuery } from '../api';
import { Interval, RawDataRow } from '../types';

type StatusResult = {
  color: 'success' | 'warning' | 'error';
  label: string;
} | null;

const getStatus = (value: number, category: Category, measure: Measure, unit: Unit): StatusResult => {
  if (category === Category.air) {
    if (measure === Measure.temperature) {
      const low = unit === Unit.fahrenheit ? 65 : 18;
      const comfortHigh = unit === Unit.fahrenheit ? 78 : 25.5;
      const warnHigh = unit === Unit.fahrenheit ? 85 : 29.5;
      if (value < low) return { color: 'warning', label: 'Cool' };
      if (value <= comfortHigh) return { color: 'success', label: 'Comfortable' };
      if (value <= warnHigh) return { color: 'warning', label: 'Warm' };
      return { color: 'error', label: 'Hot' };
    }
    if (measure === Measure.humidity) {
      if (value < 25) return { color: 'error', label: 'Very Dry' };
      if (value < 30) return { color: 'warning', label: 'Dry' };
      if (value <= 60) return { color: 'success', label: 'Good' };
      if (value <= 70) return { color: 'warning', label: 'Humid' };
      return { color: 'error', label: 'Very Humid' };
    }
    if (measure === Measure.co2) {
      if (value < 1000) return { color: 'success', label: 'Good' };
      if (value < 1500) return { color: 'warning', label: 'Elevated' };
      return { color: 'error', label: 'High' };
    }
    if (measure === Measure.pm2_5) {
      if (value < 12) return { color: 'success', label: 'Good' };
      if (value < 35) return { color: 'warning', label: 'Moderate' };
      return { color: 'error', label: 'Unhealthy' };
    }
    if (measure === Measure.voc) {
      if (value < 300) return { color: 'success', label: 'Good' };
      if (value < 1000) return { color: 'warning', label: 'Moderate' };
      return { color: 'error', label: 'Poor' };
    }
  }
  return null;
};

type Props = {
  category: Category;
  measure: Measure;
  icon: ReactNode;
  onClick?: () => void;
  selected?: boolean;
};

const MetricCard = ({ category, measure, icon, onClick, selected }: Props) => {
  const userTenant = useAppSelector((state: RootState) => state.userTenant);
  const tenant = useAppSelector((state: RootState) => state.tenant);
  const home = useAppSelector((state: RootState) => state.property);
  const userPreferences = useAppSelector((state: RootState) => state.preferences);

  const currentTenant = useMemo(() => tenant.currentTenant || userTenant, [tenant.currentTenant, userTenant]);
  const propertyId = useMemo(() => home?.property_id || '', [home?.property_id]);
  const manifestEntries = useMemo(() => home.manifestEntries || [], [home.manifestEntries]);
  const devices = useMemo(() => home.devices || [], [home.devices]);
  const preferences = useMemo(() => userPreferences.preferences, [userPreferences.preferences]);
  const unitPreferences = useMemo(() => preferences.units || {}, [preferences.units]);

  const preferredUnit = useMemo(() => (
    unitPreferences?.[formatCategoryMeasurePair(category, measure)]
    || getUnitsByCategoryMeasure(category, measure)[0]
  ), [category, measure, unitPreferences]);

  const matchingSensors = useCallback(
    (ss: Sensor) => ss.sensor_category === category && ss.measure_name === measure,
    [category, measure]
  );

  // Average of latest readings across all rooms
  const currentAvg = useMemo(() => {
    const entries = manifestEntries.filter(e =>
      (e.device as ManifestDevice)?.sensors?.some(matchingSensors)
    );
    const values = entries
      .map(cu => {
        const sensor = (cu.device as ManifestDevice).sensors.find(matchingSensors);
        const entityId = cu.sensor_map?.[sensor?.sensor_id ?? sensor?.friendly_name ?? ''] || '';
        const sorted = devices
          .filter(d => d.data.device_id === cu.gateway_device_id && d.data.entity_id === entityId)
          .sort((a, b) => a.datetime - b.datetime);
        return sorted?.[0]?.data.value;
      })
      .filter(v => v !== undefined && !isNaN(Number(v)))
      .map(v => convertValue(Number(v), category, measure, preferredUnit));

    if (!values.length) return null;
    return values.reduce((sum, v) => sum + v, 0) / values.length;
  }, [category, devices, manifestEntries, matchingSensors, measure, preferredUnit]);

  // 7-day sparkline data (averaged across sensors per bucket)
  const { currentData: rawData, isFetching } = useIntervalReadingsQuery({
    userTenantId: currentTenant?.tenant_id || '',
    propertyId,
    category,
    measure,
    interval: Interval.WEEK,
  }, {
    skip: !currentTenant?.tenant_id || !propertyId,
  });

  const sparklineData = useMemo(() => {
    if (!rawData?.length) return [];
    const buckets: Record<string, number[]> = {};
    (rawData as RawDataRow[]).forEach(row => {
      if (!buckets[row.bucket]) buckets[row.bucket] = [];
      const v = convertValue(Number(row.value), category, measure, preferredUnit);
      if (!isNaN(v)) buckets[row.bucket].push(v);
    });
    return Object.entries(buckets)
      .sort(([a], [b]) => new Date(a).getTime() - new Date(b).getTime())
      .map(([, vals]) => vals.reduce((s, v) => s + v, 0) / vals.length);
  }, [category, measure, preferredUnit, rawData]);

  const status = useMemo(
    () => currentAvg !== null ? getStatus(currentAvg, category, measure, preferredUnit as Unit) : null,
    [category, currentAvg, measure, preferredUnit]
  );

  const sparkColor = status?.color === 'error' ? '#d32f2f'
    : status?.color === 'warning' ? '#ed6c02'
    : '#2e7d32';

  const hasData = currentAvg !== null || sparklineData.length > 0;

  const cardContent = (
    <CardContent sx={{ pb: '12px !important' }}>
      <Stack spacing={1}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={0.5}>
            {icon}
            <Typography variant="caption" color="text.secondary" fontWeight={600} letterSpacing={0.5}>
              {findMeasureLabel(measure).toUpperCase()}
            </Typography>
          </Stack>
          {status && (
            <Chip
              label={status.label}
              color={status.color}
              size="small"
              sx={{ fontSize: '0.65rem', height: 18, px: 0.5 }}
            />
          )}
        </Stack>

        {isFetching && !hasData && (
          <CircularProgress size={20} sx={{ mt: 1 }} />
        )}

        {!hasData && !isFetching && (
          <Typography variant="caption" color="text.disabled">No data</Typography>
        )}

        {hasData && (
          <Stack direction="row" alignItems="flex-end" justifyContent="space-between" spacing={1}>
            <Typography variant="h5" fontWeight={700} lineHeight={1.1} sx={{ minWidth: 0 }}>
              {currentAvg !== null
                ? `${formatValue(currentAvg, 1)}${findUnitsLabel(preferredUnit)}`
                : '—'}
            </Typography>
            {sparklineData.length > 1 && (
              <Chart
                type="area"
                height={48}
                width={100}
                series={[{ name: findMeasureLabel(measure), data: sparklineData }]}
                options={{
                  chart: {
                    sparkline: { enabled: true },
                    animations: { enabled: false },
                  },
                  stroke: { curve: 'smooth', width: 2 },
                  fill: {
                    type: 'gradient',
                    gradient: { opacityFrom: 0.25, opacityTo: 0 },
                  },
                  tooltip: { enabled: false },
                  colors: [sparkColor],
                } as any}
              />
            )}
          </Stack>
        )}
      </Stack>
    </CardContent>
  );

  return (
    <Card
      variant={selected ? 'outlined' : 'elevation'}
      sx={{
        height: '100%',
        outline: selected ? 2 : 0,
        outlineColor: 'primary.main',
      }}
    >
      {onClick ? (
        <CardActionArea onClick={onClick} sx={{ height: '100%', alignItems: 'flex-start' }}>
          {cardContent}
        </CardActionArea>
      ) : cardContent}
    </Card>
  );
};

export default MetricCard;
